import {
    useMemo,
} from 'react'
import useGetAccount from 'app/requests/Account/hooks/useGetAccount'
import {
    AccountLane,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.types'
import useGetCountry from 'app/Apps/ContactManagement/Countries/hooks/useGetCountry'
import useGetLocation from 'app/Apps/ContactManagement/Locations/hooks/useGetLocation'
import {
    LaneStatus,
    LeaseType, ProductTypeCQuote, TemperatureRange,
} from 'app/enums/label.enums'
import useGetRateCardsData from 'app/requests/RateCard/hooks/useGetRateCardsData'
import {
    tempRangeProductTypeMap,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.utils'

// used for non-strategic accounts for direct lease and C, CRT product types
export default function useAlternativeFlowRequests(value: AccountLane, disabled: boolean) {
    const {
        data: account,
    } = useGetAccount(value?.account?.id)

    const isAlternativeFlow = useMemo(() => {
        return !disabled && !account?.isStrategicAccount
            && value?.leaseType === LeaseType.DIRECT_LEASE
            && [
                TemperatureRange.C,
                TemperatureRange.CRT,
            ].includes(value?.temperatureRange)
            && (value?.id ? [
                LaneStatus.TO_BE_QUOTED,
                LaneStatus.UNDER_REVIEW_PRICING,
            ].includes(value?.laneStatus) : true)
    }, [
        account?.isStrategicAccount,
        disabled,
        value?.id,
        value?.laneStatus,
        value?.leaseType,
        value?.temperatureRange,
    ])

    const {
        data: originAirportData,
    } = useGetLocation(isAlternativeFlow && value?.originAirport?.id)

    const {
        data: originCountryData,
    } = useGetCountry(originAirportData?.country?.id)

    const {
        data: destinationAirportData,
    } = useGetLocation(isAlternativeFlow && value?.destinationAirport?.id)

    const {
        data: destinationCountryData,
    } = useGetCountry(destinationAirportData?.country?.id)

    const rateCardRequestParams = useMemo(() => {
        if (isAlternativeFlow) {
            return {
                originRegion: originCountryData?.regionSecondLevel
                    || originAirportData?.regionSecondLevel,
                destinationRegion: destinationCountryData?.regionSecondLevel
                    || destinationAirportData?.regionSecondLevel,
                productType: tempRangeProductTypeMap[value?.temperatureRange] as ProductTypeCQuote,
            }
        }
        return undefined
    }, [
        destinationAirportData?.regionSecondLevel,
        destinationCountryData?.regionSecondLevel,
        isAlternativeFlow,
        originAirportData?.regionSecondLevel,
        originCountryData?.regionSecondLevel,
        value?.temperatureRange,
    ])

    const {
        data: logisticDetails,
    } = useGetRateCardsData(rateCardRequestParams)

    return {
        isAlternativeFlow,
        logisticDetails,
    }
}
